import { DAY_MAP } from "@/lib/schedule"
import { CouponAvailability, CouponAvailabilityBlock } from "@/types/external/coupon-response"

/**
 * UX HINT ONLY — evaluated against the CUSTOMER's browser clock, not the
 * tenant's timezone. The server (coupons/evaluate at checkout, and the saga
 * behind it) is authoritative and
 * evaluates against the tenant's wall-clock (tenant_config, fallback
 * America/Sao_Paulo). This only saves the customer a round trip for the
 * common case; it must never be trusted to actually gate the order.
 *
 * Empty/absent `blocks` = always available (retrocompat with coupons that
 * predate this feature). A block whose `days` includes today and whose
 * `schedules` is empty is treated as open all day within that day (a
 * day-only restriction with no shift narrowing it further).
 *
 * Day names are uppercase English ("MONDAY".."SUNDAY"), matched via DAY_MAP.
 * Shift bounds are "HH:MM:SS" strings compared lexicographically — inclusive
 * on both ends, full precision (unlike the minute-truncated comparisons in
 * `@/lib/schedule`, which govern store/item hours, not this wire contract).
 */
export function isCouponAvailableNow(
  availability: CouponAvailability | undefined,
  now: Date = new Date(),
): boolean {
  const blocks = availability?.blocks
  if (!blocks || blocks.length === 0) return true

  const currentDay = DAY_MAP[now.getDay()]
  const currentTime = now.toTimeString().slice(0, 8) 

  return blocks.some((block) => {
    if (!block.days.includes(currentDay)) return false
    if (!block.schedules || block.schedules.length === 0) return true
    return block.schedules.some((s) => currentTime >= s.start && currentTime <= s.end)
  })
}

const DAY_ABBREVIATION: Record<string, string> = {
  MONDAY: "seg",
  TUESDAY: "ter",
  WEDNESDAY: "qua",
  THURSDAY: "qui",
  FRIDAY: "sex",
  SATURDAY: "sáb",
  SUNDAY: "dom",
}

const WEEK_ORDER = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
]

function formatDayList(days: string[]): string {
  const abbreviations = WEEK_ORDER.filter((d) => days.includes(d)).map((d) => DAY_ABBREVIATION[d])
  if (abbreviations.length === 0) return ""
  if (abbreviations.length === 1) return abbreviations[0]
  return `${abbreviations.slice(0, -1).join(", ")} e ${abbreviations[abbreviations.length - 1]}`
}

function isWholeDayBlock(block: CouponAvailabilityBlock): boolean {
  if (!block.schedules || block.schedules.length === 0) return true
  return block.schedules.every((s) => s.start === "00:00:00" && s.end === "23:59:59")
}

function formatShiftTimes(schedules: CouponAvailabilityBlock["schedules"]): string {
  return schedules.map((s) => `das ${s.start.slice(0, 5)} às ${s.end.slice(0, 5)}`).join(" e ")
}

/**
 * Shared by the friendly toast message and the compact showcase hint: the
 * days (and, when narrower than a whole day, the shift) each block opens.
 * Null when there is no window to describe.
 */
function formatAvailabilityWindow(availability: CouponAvailability | undefined): string | null {
  const blocks = availability?.blocks ?? []
  if (blocks.length === 0) return null

  const parts = blocks
    .map((block) => {
      const days = formatDayList(block.days)
      if (!days) return null
      return isWholeDayBlock(block) ? days : `${days} (${formatShiftTimes(block.schedules)})`
    })
    .filter((p): p is string => Boolean(p))

  return parts.length > 0 ? parts.join("; ") : null
}

/**
 * Friendly PT-BR explanation of an out-of-window coupon, naming the days it
 * DOES work. Shift times are added only for a block that is not open all day.
 */
export function formatCouponAvailabilityMessage(availability: CouponAvailability | undefined): string {
  const window = formatAvailabilityWindow(availability)
  return window ? `Esse cupom vale apenas ${window}.` : "Esse cupom está fora do período de disponibilidade."
}

/**
 * Compact showcase hint — same day/shift wording as
 * {@link formatCouponAvailabilityMessage}, without the sentence around it.
 * Null when the coupon has no window (always available).
 */
export function formatCouponAvailabilityHint(availability: CouponAvailability | undefined): string | null {
  const window = formatAvailabilityWindow(availability)
  return window ? `Válido ${window}` : null
}

const DAY_NAME: Record<string, string> = {
  MONDAY: "segunda",
  TUESDAY: "terça",
  WEDNESDAY: "quarta",
  THURSDAY: "quinta",
  FRIDAY: "sexta",
  SATURDAY: "sábado",
  SUNDAY: "domingo",
}

function joinWithE(parts: string[]): string {
  if (parts.length <= 1) return parts.join("")
  return `${parts.slice(0, -1).join(", ")} e ${parts[parts.length - 1]}`
}

function usageDays(days: string[]): string {
  const ordered = WEEK_ORDER.filter((day) => days.includes(day))
  if (ordered.length === 0) return ""
  if (ordered.length === WEEK_ORDER.length) return "todos os dias"
  if (ordered.length === 1) {
    const day = ordered[0]
    return `${day === "SATURDAY" || day === "SUNDAY" ? "no" : "na"} ${DAY_NAME[day]}`
  }
  return `nos dias ${joinWithE(ordered.map((day) => DAY_NAME[day]))}`
}

/**
 * The days and hours an out-of-window coupon works, as the sentence tail of
 * "Você pode usar …" — e.g. "nos dias sexta e sábado das 18:00 às 23:00".
 * Null when the coupon carries no window to describe.
 */
export function formatCouponUsageWindows(availability: CouponAvailability | null | undefined): string | null {
  const parts = (availability?.blocks ?? [])
    .map((block) => {
      const days = usageDays(block.days)
      if (!days) return null
      return isWholeDayBlock(block) ? days : `${days} ${formatShiftTimes(block.schedules)}`
    })
    .filter((part): part is string => Boolean(part))
  return parts.length > 0 ? parts.join("; ") : null
}

/** True when one of the coupon's blocks lists today, so it is the hour (not the day) that is off. */
export function couponWorksSomeTimeToday(
  availability: CouponAvailability | null | undefined,
  now: Date = new Date(),
): boolean {
  const today = DAY_MAP[now.getDay()]
  return (availability?.blocks ?? []).some((block) => block.days.includes(today))
}
